import { useEffect, useState } from 'react';
import { Eye, Users, Globe2 } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { createApiClient } from '../lib/apiClient';

const api = createApiClient();

/**
 * FounderStoryStats — inline analytics chip rendered on a Story page.
 * Only visible to founders (server returns 403 for non-founders /
 * non-admins). Uses the existing /admin/stories/:slug/analytics endpoint
 * which is admin-gated; the founder accounts are also admins (Jay) so
 * this works for them. If the call 403s we silently render nothing.
 */
const FounderStoryStats = ({ slug }) => {
  const { user } = useUserAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.is_founder || !slug) return undefined;
    let cancelled = false;
    api.get(`/admin/stories/${encodeURIComponent(slug)}/analytics`, { params: { days: 30 } })
      .then(res => { if (!cancelled) setStats(res.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [slug, user?.is_founder]);

  if (!user?.is_founder || error || !stats) return null;

  return (
    <div
      className="mt-4 inline-flex items-center gap-3 px-3 py-2 rounded-full bg-yellow-300/10 text-yellow-200 ring-1 ring-yellow-300/30 backdrop-blur-md text-xs font-bold"
      data-testid="founder-story-stats"
      title="Founders-only · 30-day analytics"
    >
      <span className="inline-flex items-center gap-1.5">
        <Eye className="w-3.5 h-3.5" />
        {stats.total_views?.toLocaleString?.() ?? stats.total_views} views
      </span>
      <span className="w-px h-3 bg-yellow-300/30" />
      <span className="inline-flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" />
        {stats.unique_visitors?.toLocaleString?.() ?? stats.unique_visitors} unique
      </span>
      {stats.top_countries?.[0]?.label && (
        <>
          <span className="w-px h-3 bg-yellow-300/30" />
          <span className="inline-flex items-center gap-1.5">
            <Globe2 className="w-3.5 h-3.5" />
            Top: {stats.top_countries[0].label}
          </span>
        </>
      )}
    </div>
  );
};

export default FounderStoryStats;
